import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Modal from 'react-modal';
import axios from 'axios';
// --- PDF Rendering ---
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
// --- DOCX Rendering ---
import { renderAsync } from 'docx-preview';

import './DocumentViewerModal.css';

// --- PDF Worker Configuration ---
const pdfWorkerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
try {
    if (pdfjs.GlobalWorkerOptions.workerSrc !== pdfWorkerSrc) {
         console.log(`DocumentViewer: Setting PDF worker source to: ${pdfWorkerSrc}`);
         pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
    }
} catch (e) {
    console.error("DocumentViewer: Error setting PDF worker source:", e);
}
// --- End PDF Worker Configuration ---


const BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// --- Helper Functions ---
const parseCsvSimple = (csvString) => {
    if (!csvString || typeof csvString !== 'string') return { headers: [], rows: [] };
    const lines = csvString.trim().split('\n');
    if (lines.length === 0) return { headers: [], rows: [] };
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const rows = lines.slice(1).map(line => {
        return line.split(',').map(cell => cell.trim().replace(/^"|"$/g, ''));
    });
    return { headers, rows };
};

const getFileType = (filePath) => {
    return filePath?.split('.').pop()?.toLowerCase() || '';
};

const isImage = (fileType) => ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(fileType);
const isDocx = (fileType) => fileType === 'docx';
const isPdf = (fileType) => fileType === 'pdf';
const isPptx = (fileType) => fileType === 'pptx'; // Added for PPTX
const isUnsupportedPreview = (fileType) => ['doc', 'ppt', 'xls', 'xlsx'].includes(fileType); // PPTX removed
const isCsv = (fileType) => fileType === 'csv';
const isTextFetchNeeded = (fileType) => 
    !isImage(fileType) && 
    !isPdf(fileType) && 
    !isDocx(fileType) &&
    !isPptx(fileType) && // Updated to exclude PPTX
    !isUnsupportedPreview(fileType);
// --- End Helper Functions ---


const DocumentViewerModal = ({ isOpen, onClose, filePath, username }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [previewUrl, setPreviewUrl] = useState('');
    const [fileType, setFileType] = useState('');

    const [textContent, setTextContent] = useState('');
    const [docxBlob, setDocxBlob] = useState(null);

    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfError, setPdfError] = useState('');
    const [pdfFileObject, setPdfFileObject] = useState(null); 

    // --- NEW: PPTX State ---
    const [pptxSlides, setPptxSlides] = useState([]);
    const [pptxSlideNumber, setPptxSlideNumber] = useState(0); // 0-indexed
    const [pptxError, setPptxError] = useState('');

    const docxContainerRef = useRef(null);
    const [docxError, setDocxError] = useState('');

    const [isDownloading, setIsDownloading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editTextContent, setEditTextContent] = useState('');
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

    const sanitizedFilePath = useMemo(() => {
        if (!filePath) {
            console.warn("DocumentViewer (Memo): filePath prop is missing or empty.");
            return '';
        }
        if (!username) {
            console.warn("DocumentViewer (Memo): username prop is missing for URL construction.");
            return ''; 
        }
        
        let path = String(filePath);
        path = path.replace(/^\/+/, '');
        
        console.log(`DocumentViewer (Memo): sanitizedFilePath (relative to Home) determined as: "${path}" from prop: "${filePath}"`);
        return path;
    }, [filePath, username]); 

    const fileName = useMemo(() => {
        const pathToParse = sanitizedFilePath || filePath || '';
        return pathToParse ? String(pathToParse).split('/').pop() : 'Document Viewer';
    }, [sanitizedFilePath, filePath]);

    const isFileEditable = useMemo(() => {
        const editableTypes = ['txt', 'md', 'json', 'xml', 'html', 'js', 'css', 'py', 'java', 'c', 'cpp', 'sh', 'bat', 'log'];
        return editableTypes.includes(fileType);
    }, [fileType]);

    const resetState = useCallback(() => {
        setIsLoading(false);
        setError('');
        setPreviewUrl('');
        setFileType('');
        setTextContent('');
        setDocxBlob(null);
        setNumPages(null);
        setPageNumber(1);
        setPdfError('');
        setDocxError('');
        setPdfFileObject(null);
        setIsDownloading(false);
    // --- NEW: Reset editing state ---
    setIsEditing(false);
    setEditTextContent('');
        // --- NEW: Reset PPTX state ---
        setPptxSlides([]);
        setPptxSlideNumber(0);
        setPptxError('');
        if (docxContainerRef.current) {
            docxContainerRef.current.innerHTML = '';
        }
    }, []);

    useEffect(() => {
        if (!isOpen) {
            resetState(); 
            return;
        }
        if (!sanitizedFilePath || !username) {
            if (isOpen && filePath && !username) { 
                console.error("DocumentViewer (Effect): Username is missing, cannot fetch file data. filePath prop:", filePath);
                setError("Cannot display file: User information is missing.");
            } else if (isOpen && !filePath) { 
                 console.warn("DocumentViewer (Effect): filePath is missing, cannot fetch file data.");
                 setError("Cannot display file: File path is missing.");
            }
            return;
        }
        
        resetState(); 

        console.log(`DocumentViewer (Effect): Processing valid sanitizedFilePath: '${sanitizedFilePath}' for user: '${username}'`);

        const currentFileType = getFileType(sanitizedFilePath);
        setFileType(currentFileType);
        console.log(`DocumentViewer (Effect): Opening file (sanitized) '${sanitizedFilePath}', type: '${currentFileType}'`);
        
        const encodedSanitizedFilePathForUrl = encodeURIComponent(sanitizedFilePath);
        const fileAccessBaseUrl = `${BASE_URL}/api/file_explorer/view_user_file/${username}/Home/${encodedSanitizedFilePathForUrl}`;
        const apiUrlForPreview = fileAccessBaseUrl; 

        const fetchTextContent = async () => {
            console.log(`DocumentViewer (FetchText): Fetching as TEXT for type '${currentFileType}': ${apiUrlForPreview}`);
            setIsLoading(true);
            try {
                const previewUrlWithCacheBust = `${apiUrlForPreview}${apiUrlForPreview.includes('?') ? '&' : '?'}_=${Date.now()}`;
                const response = await axios.get(previewUrlWithCacheBust, { withCredentials: true, responseType: 'text', headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' } });
                setTextContent(response.data || '');
            } catch (err) {
                 console.error(`DocumentViewer (FetchText): Error fetching text content for ${sanitizedFilePath}:`, err);
                 let errorMsg = `Failed to load file content. Path: ${sanitizedFilePath}`;
                 if (err.response?.status === 404) errorMsg = `File not found on server: ${sanitizedFilePath}`;
                 else if (err.response?.status === 401) errorMsg = 'Unauthorized to access file.';
                 else if (err.response?.status === 403) errorMsg = 'Forbidden to access file.';
                 else if (err.message) errorMsg += ` (${err.message})`;
                setError(errorMsg);
                setTextContent('');
            } finally {
                setIsLoading(false);
            }
        };

        const fetchPdfBlob = async () => {
            console.log(`DocumentViewer (FetchPdf): Fetching PDF as blob: ${apiUrlForPreview}`);
            setIsLoading(true);
            try {
                const previewUrlWithCacheBust = `${apiUrlForPreview}${apiUrlForPreview.includes('?') ? '&' : '?'}_=${Date.now()}`;
                const response = await axios.get(previewUrlWithCacheBust, { 
                    withCredentials: true, 
                    responseType: 'blob',
                    headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
                });
                
                // Create a blob URL for the PDF
                const pdfBlobUrl = URL.createObjectURL(response.data);
                setPdfFileObject({ url: pdfBlobUrl });
                setPreviewUrl(pdfBlobUrl);
            } catch (err) {
                console.error(`DocumentViewer (FetchPdf): Error fetching PDF blob for ${sanitizedFilePath}:`, err);
                let errorMsg = `Failed to load PDF file. Path: ${sanitizedFilePath}`;
                if (err.response?.status === 404) errorMsg = `PDF file not found on server: ${sanitizedFilePath}`;
                else if (err.response?.status === 401) errorMsg = 'Unauthorized to access PDF file.';
                else if (err.response?.status === 403) errorMsg = 'Forbidden to access PDF file.';
                else if (err.message) errorMsg += ` (${err.message})`;
                setError(errorMsg);
            } finally {
                setIsLoading(false);
            }
        };

        const fetchDocxBlob = async () => {
             console.log(`DocumentViewer (FetchDocx): Fetching DOCX as blob: ${apiUrlForPreview}`);
             setIsLoading(true);
             try {
                     const response = await axios.get(apiUrlForPreview, { withCredentials: true, responseType: 'blob' });
                setDocxBlob(response.data);
             } catch (err) {
                console.error(`DocumentViewer (FetchDocx): Error fetching docx blob for ${sanitizedFilePath}:`, err);
                let errorMsg = `Failed to load DOCX file. Path: ${sanitizedFilePath}`;
                 if (err.response?.status === 404) errorMsg = `DOCX file not found on server: ${sanitizedFilePath}`;
                 else if (err.message) errorMsg += ` (${err.message})`;
                setError(errorMsg);
                setDocxBlob(null);
             } finally {
                 setIsLoading(false);
             }
        };

        // --- NEW: Fetch logic for PPTX previews ---
        const fetchPptxPreview = async () => {
            console.log(`DocumentViewer (FetchPptx): Requesting PPTX preview for: ${sanitizedFilePath}`);
            setIsLoading(true);
            setPptxError('');
            try {
                // This endpoint is responsible for converting PPTX to images and returning URLs
                const response = await axios.post(`${BASE_URL}/api/file_explorer/preview_pptx`, {
                    filePath: sanitizedFilePath
                }, { withCredentials: true });
                
                if (response.data?.slides && response.data.slides.length > 0) {
                    setPptxSlides(response.data.slides);
                    console.log(`DocumentViewer (FetchPptx): Received ${response.data.slides.length} slides.`);
                } else {
                    throw new Error("Preview generation failed or returned no slides.");
                }
            } catch (err) {
                console.error("DocumentViewer (FetchPptx): Error fetching PPTX preview:", err);
                const errorMsg = err.response?.data?.error || err.message || "Failed to process the presentation file. It may be corrupted or in an unsupported format.";
                setPptxError(errorMsg);
                setError(errorMsg); // Also set the general error for UI feedback
            } finally {
                setIsLoading(false);
            }
        };


        if (isImage(currentFileType)) {
            const previewUrlWithCacheBust = `${apiUrlForPreview}${apiUrlForPreview.includes('?') ? '&' : '?'}_=${Date.now()}`;
            setPreviewUrl(previewUrlWithCacheBust);
            setIsLoading(false);
        } else if (isPdf(currentFileType)) {
            fetchPdfBlob();
        } else if (isDocx(currentFileType)) {
            fetchDocxBlob(); 
        } else if (isPptx(currentFileType)) {
            fetchPptxPreview(); // Call the new PPTX fetcher
        } else if (isTextFetchNeeded(currentFileType) || isCsv(currentFileType)) {
            fetchTextContent(); 
        } else if (isUnsupportedPreview(currentFileType)) {
            console.log(`DocumentViewer (Effect): File type .${currentFileType} is unsupported for inline preview.`);
            setIsLoading(false);
        } else {
             console.log(`DocumentViewer (Effect): Default setup for unknown or complex type: ${currentFileType}.`);
            setIsLoading(false);
        }

    }, [isOpen, sanitizedFilePath, username, resetState, filePath, lastUpdatedAt]); 

    useEffect(() => {
        if (fileType === 'docx' && docxBlob && docxContainerRef.current && !isLoading && isOpen) {
            console.log("DocumentViewer: Rendering DOCX blob.");
            setDocxError('');
            docxContainerRef.current.innerHTML = '<div class="doc-viewer-loading">Rendering DOCX preview...</div>';

            renderAsync(docxBlob, docxContainerRef.current, null, {
                className: "docx-wrapper", inWrapper: true, ignoreWidth: false,
                ignoreHeight: false, ignoreFonts: false, breakPages: true,
                debug: false, experimental: false,
            })
            .then(() => {
                console.log("DocumentViewer: DOCX rendered successfully.");
                 const loadingElement = docxContainerRef.current?.querySelector('.doc-viewer-loading');
                 if (loadingElement) loadingElement.remove();
            })
            .catch((renderError) => {
                console.error("DocumentViewer: Error rendering DOCX:", renderError);
                setDocxError("DOCX preview failed. The document might be complex, corrupted, or use unsupported features.");
                if (docxContainerRef.current) docxContainerRef.current.innerHTML = '';
            });
        }
    }, [fileType, docxBlob, isLoading, isOpen]);

    const onDocumentLoadSuccess = useCallback(({ numPages: nextNumPages }) => {
        console.log(`DocumentViewer: PDF loaded successfully with ${nextNumPages} pages.`);
        setNumPages(nextNumPages);
        setPageNumber(1);
        setPdfError('');
    }, []);

    const onDocumentLoadError = useCallback((pdfLoadError) => {
        console.error("DocumentViewer: Error loading PDF document structure:", pdfLoadError);
        let detailedErrorMessage = pdfLoadError.message || "Unknown PDF loading error.";
        if (pdfLoadError.name === 'WorkerMessageHandlerException' || detailedErrorMessage.includes("worker")) {
            detailedErrorMessage = `PDF Worker Error: ${detailedErrorMessage}. Ensure 'pdf.worker.min.js' is correctly placed in your public folder and the path in DocumentViewerModal.js is correct.`;
        } else if (pdfLoadError.name === 'InvalidPDFException') {
            detailedErrorMessage = `Invalid PDF file: ${detailedErrorMessage}. The file might be corrupted or not a valid PDF.`;
        }
        setPdfError(`Failed to load PDF: ${detailedErrorMessage}`);
        setNumPages(null);
    }, []);

    const onPageLoadError = useCallback((pageLoadError) => {
        console.error(`DocumentViewer: Error loading PDF page ${pageNumber}:`, pageLoadError);
        let detailedPageErrorMessage = pageLoadError.message || "Unknown error loading PDF page.";
         if (detailedPageErrorMessage.includes("Transport destroyed")) {
             detailedPageErrorMessage += " This can happen if the PDF data stream was interrupted (check network and server logs for the file request), the file is invalid, or the component re-rendered unexpectedly during loading. Ensure the PDF URL is stable.";
         }
        setPdfError(`Error loading page ${pageNumber}: ${detailedPageErrorMessage}`);
    }, [pageNumber]);

    const goToPrevPage = () => setPageNumber(prev => Math.max(1, prev - 1));
    const goToNextPage = () => setPageNumber(prev => Math.min(numPages || 1, prev + 1));

    // --- NEW: PPTX Slide Navigation ---
    const goToPrevSlide = () => setPptxSlideNumber(s => Math.max(0, s - 1));
    const goToNextSlide = () => setPptxSlideNumber(s => Math.min((pptxSlides?.length || 1) - 1, s + 1));

    const csvData = useMemo(() => {
        if (isCsv(fileType) && textContent && !isLoading) {
            try { return parseCsvSimple(textContent); }
            catch(e) { console.error("Error parsing CSV content:", e); setError("Failed to parse CSV data."); return null; }
        }
        return null;
    }, [fileType, textContent, isLoading]);
    
    const handleDownload = useCallback(async () => {
        if (!sanitizedFilePath || !username || !fileName) {
            setError("Cannot download: file path or user context is missing.");
            return;
        }
        
        setIsDownloading(true);
        setError(''); // Clear previous errors

        const downloadUrl = `${BASE_URL}/api/file_explorer/download_user_file/${encodeURIComponent(sanitizedFilePath)}`;
        console.log(`DocumentViewer: Initiating download from URL: ${downloadUrl}`);

        try {
            const response = await axios.get(downloadUrl, {
                withCredentials: true,
                responseType: 'blob',
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            
            document.body.appendChild(link);
            link.click();
            
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            console.log("DocumentViewer: Download successful.");

        } catch (err) {
            console.error("DocumentViewer: Download failed.", err);
            let errorMsg = `Failed to download file: ${fileName}.`;
            if (err.response) {
                if (err.response.data instanceof Blob && err.response.data.type.includes('application/json')) {
                    const errorText = await err.response.data.text();
                    try {
                        const errorJson = JSON.parse(errorText);
                        errorMsg = errorJson.error || `Server responded with status ${err.response.status}.`;
                    } catch (e) {
                        errorMsg = `Server responded with status ${err.response.status} and non-JSON error.`;
                    }
                } else {
                    if (err.response.status === 404) errorMsg = `File not found on server.`;
                    else if (err.response.status === 403) errorMsg = `Access denied to download this file.`;
                    else { errorMsg = `Server error: ${err.response.statusText} (${err.response.status})`; }
                }
            } else if (err.request) {
                errorMsg = `Download failed: No response from server. Check network connection.`;
            } else {
                errorMsg = `Download failed: ${err.message}`;
            }
            setError(errorMsg);
        } finally {
            setIsDownloading(false);
        }
    }, [sanitizedFilePath, username, fileName]);


    const handleContentUpdate = useCallback(() => {
        if (!sanitizedFilePath || !username || !fileName) {
            setError("Cannot update: file path or user context is missing.");
            return;
        }

        const newContent = editTextContent;
        if (newContent === null || newContent === undefined) {
            setError("Cannot update: text content is empty or undefined.");
            return;
        }

        if (!['txt', 'md', 'json', 'xml', 'html', 'js', 'css', 'py', 'java', 'c', 'cpp', 'sh', 'bat', 'log'].includes(fileType)) {
            setError(`Editing not supported for .${fileType} files.`);
            return;
        }
        
        const payload = { filePath: sanitizedFilePath, newContent };
        const updateUrl = `${BASE_URL}/api/file_explorer/update_txt_file`;
        console.log(`DocumentViewer: Initiating content update to file: ${sanitizedFilePath}`);
        setIsLoading(true);
        setError('');

        axios.post(updateUrl, payload, { withCredentials: true })
            .then((res) => {
                console.log("DocumentViewer: File content updated successfully.", res?.data);
                // Update the displayed text and exit editing mode
                setTextContent(newContent);
                setIsEditing(false);
                // Clear any in-memory preview caches so reopening will fetch fresh data
                if (previewUrl) {
                    try { URL.revokeObjectURL(previewUrl); } catch (e) { /* ignore */ }
                    setPreviewUrl('');
                }
                if (pdfFileObject?.url) {
                    try { URL.revokeObjectURL(pdfFileObject.url); } catch (e) { /* ignore */ }
                    setPdfFileObject(null);
                }
                // Keep docxBlob and pptxSlides cached because those file types are not editable
                setLastUpdatedAt(Date.now());
            })
            .catch((err) => {
                console.error("DocumentViewer: Content update failed.", err);
                let errorMsg = `Failed to update file: ${fileName}.`;
                if (err.response) {
                    if (err.response.data instanceof Blob && err.response.data.type.includes('application/json')) {
                        err.response.data.text().then((errorText) => {
                            try {
                                const errorJson = JSON.parse(errorText);
                                errorMsg = errorJson.error || `Server responded with status ${err.response.status}.`;
                            } catch (e) {
                                errorMsg = `Server responded with status ${err.response.status} and non-JSON error.`;
                            }
                            setError(errorMsg);
                        });
                    } else {
                        if (err.response.status === 404) errorMsg = `File not found on server.`;
                        else if (err.response.status === 403) errorMsg = `Access denied to update this file.`;
                        else { errorMsg = `Server error: ${err.response.statusText} (${err.response.status})`; }
                        setError(errorMsg);
                    }
                } else if (err.request) {
                    errorMsg = `Update failed: No response from server. Check network connection.`;
                    setError(errorMsg);
                } else {
                    errorMsg = `Update failed: ${err.message}`;
                    setError(errorMsg);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [sanitizedFilePath, username, fileName, editTextContent, fileType]);

    const handleStartEditing = useCallback(() => {
        if (!['txt', 'md', 'json', 'xml', 'html', 'js', 'css', 'py', 'java', 'c', 'cpp', 'sh', 'bat', 'log'].includes(fileType)) {
            setError(`Editing not supported for .${fileType} files.`);
            return;
        }
        setEditTextContent(textContent || '');
        setIsEditing(true);
        setError('');
    }, [fileType, textContent]);

    const handleCancelEditing = useCallback(() => {
        setEditTextContent('');
        setIsEditing(false);
        setError('');
    }, []);

    const renderContent = () => {
        if (isLoading) {
            return <div className="doc-viewer-loading">Loading content...</div>;
        }
        // Show general error, but allow PPTX preview to show even if a subsequent action (like download) fails
        if (error && !pptxSlides.length) { 
            return <div className="doc-viewer-error">{error}</div>;
        }
        if (!sanitizedFilePath || !fileType) {
            return <div className="doc-viewer-error">No file selected or file type undetermined.</div>;
        }

        if (isImage(fileType)) {
            if (!previewUrl) return <div className="doc-viewer-error">Image URL not available for preview.</div>;
            return (
                <div className="doc-content-image-container">
                    <img src={previewUrl} alt={`Preview: ${fileName}`} className="doc-content-image"
                         onError={(e) => { setError('Failed to load image preview.'); console.error(`Failed to load image: ${previewUrl}`); }} />
                </div>
            );
        }
        if (isPdf(fileType)) {
            if (!pdfFileObject || !pdfFileObject.url) return <div className="doc-viewer-loading">Preparing PDF viewer... (Check URL)</div>;
            return (
                <div className="doc-content-pdf-container">
                    <div className="pdf-controls">
                        <button onClick={goToPrevPage} disabled={pageNumber <= 1}>‹ Prev</button>
                        <span>Page {pageNumber} of {numPages ?? '...'}</span>
                        <button onClick={goToNextPage} disabled={!numPages || pageNumber >= numPages}>Next ›</button>
                    </div>
                    {pdfError && <div className="pdf-error">{pdfError}</div>}
                    {!pdfError && (
                         <div className="pdf-page-container">
                            <Document
                                key={pdfFileObject.url} 
                                file={pdfFileObject} 
                                onLoadSuccess={onDocumentLoadSuccess}
                                onLoadError={onDocumentLoadError}
                                loading={<div className="pdf-loading">Loading PDF document...</div>}
                                error={<div className="pdf-error">Error preparing PDF document. Check console.</div>}
                            >
                                {numPages && (
                                    <Page
                                        key={`${pdfFileObject.url}-page-${pageNumber}`}
                                        pageNumber={pageNumber}
                                        renderTextLayer={true}
                                        renderAnnotationLayer={true}
                                        loading={<div className="pdf-loading">Rendering page {pageNumber}...</div>}
                                        onLoadError={onPageLoadError}
                                        onRenderError={(renderError) => {
                                            console.error(`Error rendering PDF page ${pageNumber}:`, renderError);
                                            setPdfError(`Error rendering page ${pageNumber}.`);
                                        }}
                                    />
                                )}
                            </Document>
                        </div>
                    )}
                </div>
            );
        }
        if (isDocx(fileType)) {
            return (
                 <div className="doc-content-docx-container" ref={docxContainerRef}>
                    {isLoading && !docxBlob && <div className="doc-viewer-loading">Loading DOCX document...</div>}
                    {docxError && !isLoading && <div className="doc-viewer-error">{docxError}</div>}
                 </div>
            );
        }
        // --- NEW: PPTX Rendering Logic ---
        if (isPptx(fileType)) {
            if (pptxError && pptxSlides.length === 0) {
                 return <div className="doc-viewer-error">{pptxError}</div>;
            }
            if (pptxSlides.length === 0 && !isLoading) {
                return <div className="doc-viewer-loading">Generating presentation preview...</div>;
            }
            if (pptxSlides.length > 0) {
                return (
                    <div className="doc-content-pptx-container">
                        <div className="pptx-controls pdf-controls">
                            <button onClick={goToPrevSlide} disabled={pptxSlideNumber <= 0}>‹ Prev</button>
                            <span>Slide {pptxSlideNumber + 1} of {pptxSlides.length}</span>
                            <button onClick={goToNextSlide} disabled={pptxSlideNumber >= pptxSlides.length - 1}>Next ›</button>
                        </div>
                         {pptxError && <div className="pdf-error">{pptxError}</div>}
                        <div className="pptx-slide-container doc-content-image-container">
                            <img 
                                src={`${BASE_URL}${pptxSlides[pptxSlideNumber]}`} 
                                alt={`Slide ${pptxSlideNumber + 1} of ${fileName}`} 
                                className="pptx-slide-image doc-content-image"
                                onError={() => setPptxError(`Failed to load slide ${pptxSlideNumber + 1}. The image may be missing or corrupt.`)}
                            />
                        </div>
                    </div>
                );
            }
        }
        if (isCsv(fileType)) {
             if (isLoading) return <div className="doc-viewer-loading">Loading CSV data...</div>;
             if (!csvData && !error) return <div className="doc-viewer-error">Error parsing CSV data or file is empty.</div>;
             if (!csvData?.headers?.length && !csvData?.rows?.length && !error) return <div className="doc-viewer-loading">CSV file appears to be empty.</div>;
             if (!csvData) return null; 
            return (
                <div className="doc-content-csv">
                    <table>
                        <thead><tr>{csvData.headers.map((h, i) => <th key={`h-${i}`}>{h}</th>)}</tr></thead>
                        <tbody>{csvData.rows.map((r, i) => <tr key={`r-${i}`}>{r.map((c, j) => <td key={`c-${i}-${j}`}>{c}</td>)}{r.length < csvData.headers.length && Array.from({ length: csvData.headers.length - r.length }).map((_, k) => <td key={`e-${i}-${k}`}></td>)}</tr>)}</tbody>
                    </table>
                </div>
            );
        }
        if (isUnsupportedPreview(fileType)) {
            return (
                <div className="doc-content-unsupported-format">
                    <p><strong>Live Preview Not Supported</strong></p>
                    <p>Browser preview for <strong>.{fileType}</strong> files is not available.</p>
                    <button 
                        className="doc-viewer-download-button"
                        onClick={handleDownload}
                        disabled={isDownloading}
                    > 
                        {isDownloading ? 'Downloading...' : 'Download File'}
                    </button>
                </div>
            );
        }
        if (textContent || isEditing) {
             const editableTypes = ['txt', 'py', 'js', 'css', 'html', 'xml', 'json', 'log', 'md', 'sh', 'bat', 'java', 'c', 'cpp'];
             const isEditable = editableTypes.includes(fileType);
             return (
                <div className="doc-content-text">
                    {!['txt', 'py', 'js', 'css', 'html', 'xml', 'json', 'log', 'md', 'sh', 'bat'].includes(fileType) && !isEditing && (
                         <p className="doc-content-default-warning">Displaying file with extension <strong>.{fileType || 'unknown'}</strong> as plain text.</p>
                    )}

                    {isEditing ? (
                        <div className="doc-editor-container">
                            <textarea
                                value={editTextContent}
                                onChange={(e) => setEditTextContent(e.target.value)}
                                className="doc-editor-textarea"
                                rows={20}
                            />
                            <div className="doc-editor-actions">
                                <button onClick={handleContentUpdate} disabled={isLoading} className="doc-editor-update">{isLoading ? 'Updating...' : 'Update'}</button>
                                <button onClick={handleCancelEditing} disabled={isLoading} className="doc-editor-cancel">Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <pre><code>{textContent || '(File appears empty)'}</code></pre>
                            {/* {isEditable && (
                                <div className="doc-editor-toggle">
                                    <button onClick={handleStartEditing} className="doc-editor-start">Edit</button>
                                </div>
                            )} */}
                        </div>
                    )}
                </div>
            );
        }
        if (!isLoading && !error) { 
            return (
                 <div className="doc-content-unsupported-format">
                    <p>Preview for <strong>.{fileType || 'unknown'}</strong> files is not available.</p>
                    <p>File: {fileName}</p>
                     <button 
                        className="doc-viewer-download-button"
                        onClick={handleDownload}
                        disabled={isDownloading}
                    > 
                        {isDownloading ? 'Downloading...' : 'Download File'}
                    </button>
                 </div>
            );
        }
        return null; 
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            overlayClassName="doc-viewer-modal-overlay"
            className="doc-viewer-modal-content"
            contentLabel="Document Viewer"
            ariaHideApp={false}
        >
            <div className="doc-viewer-modal-header">
                <h2 title={sanitizedFilePath || ''}>{fileName}</h2>
                <div className="doc-viewer-header-actions">
                    <button 
                        className="doc-viewer-download-button" 
                        onClick={handleDownload}
                        disabled={isDownloading || isLoading || !!error} // Disable if loading preview or during download or if there's a general error
                        title={error ? error : (isDownloading ? 'Download in progress...' : 'Download this file')}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                        </svg>
                        {isDownloading ? 'Downloading...' : 'Download'}
                    </button>
                    <button
                        onClick={() => { if (isEditing) { handleContentUpdate(); } else { handleStartEditing(); } }}
                        className="doc-viewer-edit-button"
                        title={isEditing ? 'Save changes' : 'Edit Content'}
                        disabled={!isFileEditable || isLoading}
                    >
                        {isEditing ? 'Save' : 'Edit'}
                    </button>
                    <button onClick={onClose} className="modal-close-button" title="Close Viewer">×</button>
                </div>
            </div>
            <div className="doc-viewer-modal-body">
                {renderContent()}
            </div>
        </Modal>
    );
};

export default DocumentViewerModal;